import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { MoreHorizontal, Plus } from 'lucide-react';
import type { RootState } from '~/redux/store';
import { Button } from '~/components/ui/buttons/Button';
import {
  useTable,
  type TableColumn,
  type TableRow,
} from '~/components/ui/table';
import OrganizationUsersCard from '~/components/common/OrganizationUsersCard';
import { extractGraphQLErrorMessages, type ClientError } from '~/utils';
import {
  useTenantUsers,
  useSetTenantUserRole,
  useChangeTenantUserTradeRole,
  useReactivateTenantUser,
  useResendTenantUserInvite,
} from '~/api/users';
import { UserStatus } from '~/graphql/graphql';
import { useIsTraderEnabled } from '~/hooks/useIsTraderEnabled';
import { useUserRoleLabels } from '~/hooks/useUserRoleLabels';
import {
  EMPTY_CELL,
  userPhoneAndLastEditCells,
} from '~/components/common/userLastEditCells';
import ContactModal from '~/pages/linked-organizations/sections/ContactModal';
import {
  CLOSED_CONTACT_MODAL,
  contactBeingEdited,
  toContactRowData,
  type ContactModalState,
} from '~/pages/linked-organizations/sections/contactModalState';
import { UpsertUserModal } from './sections/UpsertUserModal';
import {
  DeactivateUserDialog,
  type TenantUserRowData,
} from './sections/DeactivateUserDialog';

type RowAction = {
  label: string;
  variant: 'ghost-primary' | 'destructive-outline';
  onClick: () => void;
};

const feedback = (successMessage: string) => ({
  onSuccess: () => toast.success(successMessage),
  onError: (error: ClientError) =>
    toast.error(extractGraphQLErrorMessages(error).join(', ')),
});

function ActionsMenu({
  actions,
  label,
}: {
  actions: RowAction[];
  label: string;
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={label}
          className="w-12 h-8 flex items-center justify-center bg-neut-100 text-neut-600 hover:bg-neut-200 hover:text-neut-800 rounded-md shadow-sm transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-prim"
        >
          <MoreHorizontal size={16} />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side="bottom"
          align="end"
          sideOffset={4}
          className="min-w-[160px] bg-neut-0 border border-neut-200 rounded-lg shadow-drop py-1 z-50"
        >
          {actions.map((action) => (
            <DropdownMenu.Item
              key={action.label}
              className={`px-3 py-2 text-sm cursor-pointer outline-none select-none ${
                action.variant === 'destructive-outline'
                  ? 'text-error-600 hover:bg-error-100 data-[highlighted]:bg-error-100'
                  : 'text-neut-700 hover:bg-neut-100 data-[highlighted]:bg-neut-100'
              }`}
              onSelect={action.onClick}
            >
              {action.label}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export default function Users() {
  const { t } = useTranslation('users');
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [isAddOpen, setAddOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState<TenantUserRowData | null>(null);
  const [contactModalState, setContactModalState] =
    useState<ContactModalState>(CLOSED_CONTACT_MODAL);
  const contactToEdit = contactBeingEdited(contactModalState);
  const [userToDeactivate, setUserToDeactivate] =
    useState<TenantUserRowData | null>(null);
  const { data } = useTenantUsers();
  const { mutate: setRole } = useSetTenantUserRole();
  const { mutate: changeTradeRole } = useChangeTenantUserTradeRole();
  const { mutate: reactivate } = useReactivateTenantUser();
  const { mutate: resendInvite } = useResendTenantUserInvite();
  const isTraderRoleEnabled = useIsTraderEnabled();
  const roleLabels = useUserRoleLabels();

  const tableControls = useTable({});

  const columns: TableColumn[] = [
    { header: t('users.table.name'), key: 'name', width: 12 },
    { header: t('users.table.email'), key: 'email', width: 14 },
    { header: t('users.table.employeeId'), key: 'employeeId', width: 8 },
    { header: t('users.table.phoneNumber'), key: 'phoneNumber', width: 7 },
    { header: t('users.table.role'), key: 'role', width: 10 },
    { header: t('users.table.status'), key: 'status', width: 10 },
    { header: t('users.table.lastEditedBy'), key: 'lastEditedBy', width: 11 },
    { header: t('users.table.lastEditedAt'), key: 'lastEditedAt', width: 11 },
    {
      header: t('users.table.actions'),
      key: 'actions',
      width: 8,
      stickTo: 'right',
    },
  ];

  const tradeRoleActionLabels = {
    BUYER: t('users.action.makeBuyer'),
    SELLER: t('users.action.makeSeller'),
    TRADER: t('users.action.makeTrader'),
  } as const;
  const tradeRoles: Array<keyof typeof tradeRoleActionLabels> =
    isTraderRoleEnabled ? ['BUYER', 'SELLER', 'TRADER'] : ['BUYER', 'SELLER'];

  const rows: TableRow[] = (data?.users ?? []).map((user) => {
    const isSelf = user.id === currentUser?.id;
    const isAdmin = user.role === 'TENANT_ADMIN';
    const updated = () => feedback(t('users.toast.updated'));

    const actions: RowAction[] = [
      {
        label: t('users.action.edit'),
        variant: 'ghost-primary',
        onClick: () =>
          setUserToEdit({
            id: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            employeeId: user.employeeId,
            phoneNumber: user.phoneNumber,
          }),
      },
    ];

    // Offer a switch to every trade role except the user's current one.
    const addTradeRoleSwitches = (
      apply: (role: keyof typeof tradeRoleActionLabels) => void
    ) => {
      for (const role of tradeRoles.filter((target) => target !== user.role)) {
        actions.push({
          label: tradeRoleActionLabels[role],
          variant: 'ghost-primary',
          onClick: () => apply(role),
        });
      }
    };

    if (!isSelf && user.status !== 'DISABLED') {
      if (isAdmin) {
        addTradeRoleSwitches((role) =>
          setRole({ userId: user.id, role }, updated())
        );
      } else {
        actions.push({
          label: t('users.action.makeAdmin'),
          variant: 'ghost-primary',
          onClick: () =>
            setRole({ userId: user.id, role: 'TENANT_ADMIN' }, updated()),
        });
        addTradeRoleSwitches((role) =>
          changeTradeRole({ userId: user.id, role }, updated())
        );
      }
      actions.push({
        label: t('users.action.deactivate'),
        variant: 'destructive-outline',
        onClick: () => setUserToDeactivate(user),
      });
    }

    if (user.status === 'DISABLED') {
      actions.push({
        label: t('users.action.reactivate'),
        variant: 'ghost-primary',
        onClick: () => reactivate({ userId: user.id }, updated()),
      });
    }

    if (user.status === 'INVITED') {
      actions.push({
        label: t('users.action.resendInvite'),
        variant: 'ghost-primary',
        onClick: () =>
          resendInvite(
            { userId: user.id },
            feedback(t('users.toast.inviteSent'))
          ),
      });
    }

    return {
      id: { value: user.id },
      name: { value: `${user.firstName} ${user.lastName}` },
      email: { value: user.externalId ?? EMPTY_CELL },
      employeeId: { value: user.employeeId ?? '' },
      role: { value: roleLabels[user.role] },
      status: { value: t(`users.status.${user.status}`) },
      ...userPhoneAndLastEditCells(user),
      actions: {
        value:
          user.status === UserStatus.ContactOnly ? (
            <Button
              size="sm"
              width="auto"
              variant="ghost-primary"
              onClick={() =>
                setContactModalState({
                  mode: 'edit',
                  contact: toContactRowData(user),
                })
              }
            >
              {t('users.action.editContact')}
            </Button>
          ) : (
            <ActionsMenu actions={actions} label={t('users.table.actions')} />
          ),
      },
    };
  });

  return (
    <div className="w-full bg-neut-bg min-h-[calc(100vh-70px)]">
      <title>{t('users.title')}</title>
      <OrganizationUsersCard
        title={t('users.title')}
        addLabel={t('users.addUser')}
        onAdd={() => setAddOpen(true)}
        columns={columns}
        rows={rows}
        tableControls={tableControls}
        emptyMessage={t('users.table.empty')}
        tableName="tenant-users"
        headerActions={
          <Button
            size="sm"
            width="auto"
            variant="secondary"
            onClick={() => setContactModalState({ mode: 'new' })}
          >
            <Plus size={16} className="mr-1" />
            {t('users.addContact')}
          </Button>
        }
      />
      <UpsertUserModal
        isOpen={isAddOpen || userToEdit !== null}
        onClose={() => {
          setAddOpen(false);
          setUserToEdit(null);
        }}
        user={userToEdit}
      />
      <ContactModal
        key={contactToEdit === null ? 'new' : contactToEdit.id}
        isOpen={contactModalState.mode !== 'closed'}
        onClose={() => setContactModalState(CLOSED_CONTACT_MODAL)}
        organizationId={currentUser === null ? '' : currentUser.organization.id}
        contact={contactToEdit}
        isOwnOrganization
      />
      <DeactivateUserDialog
        user={userToDeactivate}
        onClose={() => setUserToDeactivate(null)}
      />
    </div>
  );
}
